export default {
  tabs: {
    profile: 'Profile',
    branding: 'Branding',
    domain: 'Domain',
    earning: 'Earning',
    feature_flags: 'Feature flags',
    notification_channels: 'Notification channels',
    pricing: 'Pricing',
    fees: 'Fees',
    payments: 'Payments',
    billing: 'Billing',
  },
  earning: {
    internal_notice: 'FoodAmigos-only settings. Not visible to the partner.',
    read_only_notice: 'Read-only. Click Edit to change any value.',
    editing_notice: 'Editing — changes affect real payout calculations.',
  },
  fees: {
    internal_notice:
      'FoodAmigos-only settings. Not visible to the partner, and only super admins can open this tab. Changes affect what diners are charged and what the platform earns.',
    read_only_notice: 'Read-only. Click Edit to change any value on this tab.',
    editing_notice:
      'Editing — Save writes both the service fee and the processing rates. Rates apply from the next charge on; earlier transactions keep the margin recorded against them.',
  },
  profile: {
    read_only_notice: 'Read-only. Click Edit to change any value.',
    editing_notice: 'Editing — the display name is visible to consumers.',
    form: {
      name: {
        label: 'Partner name',
        hint: 'The legal / entity name. Used internally and on billing documents.',
      },
      display_name: {
        label: 'Consumer display name',
        hint: 'The "Powered by ___" label shown to customers. Defaults to the partner name if left blank.',
      },
      admin_email: {
        label: 'Admin email',
        hint: 'The invited admin account. Cannot be changed here — contact engineering to move the account.',
      },
      invoicing_emails: {
        label: 'Invoice emails',
        hint: 'Receipts for invoices billed to this partner are sent to each of these addresses. Leave empty to use the admin email.',
        placeholder: 'Admin email',
      },
      status: {
        label: 'Status',
        hint: 'Invited partners become active on their first login — activating manually skips that. Suspending deactivates all of the partner’s team logins.',
      },
      region: {
        label: 'Region',
        hint: 'The country is fixed. Pick a state when the partner’s restaurants follow state-level tax rules — this only changes the partner’s defaults, not existing restaurants.',
      },
      messenger_audience: {
        label: 'Support chat',
        hint: 'Who under this partner gets the FoodAmigos support chat. FoodAmigos staff always have it. White-label partners support their own restaurants, so only their admins get it.',
        options: {
          everyone: 'Everyone, restaurants included',
          partner_admins: 'Partner admins only',
          nobody: 'Nobody',
        },
      },
      shop_domains: {
        label: 'Shop domains',
        hint: 'Root domains the partner’s restaurant shops are served on. The primary domain is used for new brands. The partner connects and unlinks its own shop domains; the Domain tab shows their DNS records.',
      },
    },
    region: {
      whole_country: 'Whole country',
    },
    shop_domains: {
      primary: 'Primary',
      platform_default: 'Platform default',
      empty: 'No shop domain linked yet',
    },
    statuses: {
      invited: 'Invited',
      active: 'Active',
      suspended: 'Suspended',
    },
    custody: {
      label: 'Payment processing',
      not_configured: 'Not configured yet',
      provider: 'Partner’s own gateway',
      platform: 'FoodAmigos processes payments',
      bank_account: {
        set: 'Bank account: {{label}} ({{status}})',
        missing: 'No bank account yet. The partner enters it on Partner billing.',
        statuses: {
          active: 'active',
          pending_verification: 'being verified',
          action_needed: 'action needed',
          errored: 'payout failed',
          missing: 'missing',
        },
      },
      hint: 'Decided by the payment configuration on the Payments tab and locked once a rail is saved. Under FoodAmigos processing the partner’s revenue share is paid out monthly.',
      kyc: {
        label: 'Payout account',
        missing: 'Not created yet',
        incomplete: 'Verification incomplete',
        complete: 'Ready for payouts',
        currently_due: 'Stripe still needs: {{fields}}',
        send_link: 'Send onboarding link',
        link_copied:
          'Onboarding link copied to the clipboard — forward it to the partner. It is single-use and expires.',
        link_ready: 'Onboarding link — forward it to the partner (single-use, expires)',
        link_failed: 'Could not create the onboarding link.',
      },
    },
    suspend_confirm: {
      title: 'Suspend this partner?',
      description:
        'All of the partner’s team members will be deactivated and their sessions revoked. Their restaurants stay live.',
    },
  },
  branding: {
    read_only_notice: 'Read-only. Click Edit to change any value.',
    editing_notice: "Editing — changes are visible on the partner's white-labelled surfaces.",
    upload: 'Upload logo',
    logo: {
      label: 'Logo',
      hint: "Shown on the partner's white-labelled surfaces.",
    },
    circle_logo: {
      label: 'Favicon / circle logo',
      hint: 'Square logo used as the favicon and avatar. Optional — can be removed.',
    },
    splash_image: {
      label: 'Login image',
      hint: "Fills the left half of the partner's white-label login page edge to edge (cover-cropped). Recommended: portrait composition, at least 1200×1200px. Optional — can be removed.",
    },
    footer_note: 'More branding options (secondary colours) coming later.',
  },
  revenue_split: {
    title: 'Revenue split',
    description:
      "Set the partner's share of each fee FoodAmigos collects on their restaurants' orders. Defaults to 0% — FoodAmigos keeps the full fee until a share is granted. Paid out each billing cycle.",
    manage_on_fees: 'Manage on Fees tab',
    split: 'Partner {{partner}}% · FoodAmigos {{platform}}%',
    rows: {
      processing: 'Processing fees',
      gift_card: 'Gift card fee',
      delivery: 'Delivery fees',
      service_fee: 'Service fee',
    },
    notes: {
      service_fee: 'Amount charged to diners is set under Service fee controls on the Fees tab.',
    },
  },
  service_fee_controls: {
    title: 'Service fee controls',
    description: 'What the partner charges their diners as a service fee on top of the order.',
    zero_percentage_warning:
      'A 0% percentage does not disable the service fee: computed fees below the minimum amount are rounded up to it, so every order is still charged at least the minimum.',
  },
  hard_limits: {
    title: 'Hard limits',
    description:
      'FoodAmigos ceilings enforced when saving and at checkout. No service fee on this partner — including per-restaurant overrides — may exceed these.',
    max_percentage: {
      label: 'Maximum service fee %',
      help: "The partner's service fee can't be set above this. Platform limit: 5%.",
    },
    min_amount: {
      label: 'Minimum service fee amount',
      help: "Per-restaurant minimums can't be set below this amount.",
    },
    max_amount: {
      label: 'Maximum service fee amount',
      help: 'Absolute cap on the fixed service fee, regardless of percentage.',
    },
  },
  form: {
    charges_service_fee_on_site: {
      label: 'Charge the service fee on table orders',
      description:
        "The default for this partner's restaurants; each can set its own. Off: table orders pay no service fee.",
    },
    is_service_fee_change_allowed: {
      label: 'Allow changing service fee per restaurant',
      description: "Lets the partner's individual restaurants use a different service fee.",
    },
    service_fee_platform_share: {
      label: 'Foodamigos share of the service fee',
    },
    recommended: 'Recommended: {{value}}',
    service_fee_percentage: {
      label: 'Default service fee percentage',
      help: "Applied to each of the partner's restaurants unless overridden.",
    },
    min_service_fee_amount: {
      label: 'Default minimum fee amount',
      help: 'Default minimum per order — computed fees below it are rounded up. Restaurants may set their own within the FoodAmigos limits.',
    },
    max_service_fee_amount: {
      label: 'Default maximum fee amount',
      help: 'Default cap per order — restaurants may set their own within the FoodAmigos limits.',
    },
    primary_color: {
      label: 'Primary brand colour',
    },
    dashboard_subdomain: {
      label: 'Partner login slug',
      hint: "Lowercase slug — identifies the partner's brand in its team login link (?partner=…).",
      placeholder: 'partner-name',
    },
    login_url: {
      label: 'Team login link',
      hint: "The link the partner's team uses to reach its branded login page.",
    },
    file: {
      label: 'Image',
    },
  },
  buttons: {
    edit: 'Edit',
    cancel: 'Cancel',
    save: 'Save',
  },
  toasts: {
    load_error: 'Could not load the settings.',
    failed_to_save_changes: 'Failed to save changes',
    changes_got_saved: 'Changes got saved',
    logo_uploaded: 'Logo uploaded',
    logo_upload_failed: 'Failed to upload the logo',
    logo_removed: 'Logo removed',
    logo_remove_failed: 'Failed to remove the logo',
    file_too_large: 'File is too large. Maximum size is {{max}} MB.',
    image_too_narrow: 'The login image must be at least {{min}}px wide.',
  },
  notification_channels: {
    master_locked: 'The master partner sends for every platform restaurant, so its channels cannot be changed here.',
    unavailable: 'Notification channels are not available on this backend yet.',
    notice:
      'Internal setting. A partner without its own Twilio account must stay email-only: every SMS, WhatsApp message and voice call would otherwise go out on the FoodAmigos Twilio account.',
    forbidden: 'Only super admins can change notification channels.',
    columns: {
      channel: 'Channel',
      status: 'Enabled',
    },
    channels: {
      email: {
        name: 'Email',
        description: 'Always on, so the partner and its restaurants can always be reached.',
      },
      sms: {
        name: 'SMS',
        description: 'Order updates and login codes to guests by text message.',
      },
      whatsapp: {
        name: 'WhatsApp',
        description: 'Alerts to restaurant owners and login codes to guests on WhatsApp.',
      },
      voice_call: {
        name: 'Voice calls',
        description: 'Reminder calls for pending orders and phone answering.',
      },
      push: {
        name: 'Push notifications',
        description:
          "Push notifications to guests and to the owner's dashboard app. Staff tablet alerts are not affected.",
      },
    },
    toasts: {
      load_error: 'Could not load the notification channels.',
      error: 'Could not save the notification channels.',
      saved: 'Notification channels saved.',
    },
  },
  feature_flags: {
    internal_notice: 'FoodAmigos-only settings. Not visible to the partner. Changes take effect immediately.',
    read_only:
      'Read-only — changing restaurant billing needs a coordinated rollout, not a switch. Talk to engineering.',
    forbidden: 'Only super admins can manage partner feature flags.',
    columns: {
      feature: 'Feature',
      status: 'Status',
    },
    flags: {
      external_printer: {
        name: 'External receipt printer',
        description:
          'The default for restaurants this partner onboards from now on: each new restaurant starts out able to connect a network receipt printer to the order terminal app. Existing restaurants keep whatever their own Feature Flags tab says, and every restaurant can still be switched individually there.',
      },
      plg_onboarding: {
        name: 'Self-serve (PLG) onboarding',
        description:
          'Self-serve partner onboarding: gives this partner the guided New restaurant page and onboarding wizard instead of the classic Add-Company modal and Get Started stepper, hides FoodAmigos-only and not-yet-applicable surfaces for the partner and its restaurants, and blocks the first go-live until a launch date is agreed. On for every new partner.',
      },
      issue_end_customer_receipts: {
        name: 'End-customer invoices',
        description:
          'Lets guests request an invoice at checkout and receive it by email. Off for new partners; international invoice format rules make it costly to support.',
      },
      merchant_billing: {
        name: 'Restaurant billing',
        description: 'Bills the partner’s restaurants through the platform. Off means they settle outside it.',
      },
      cuisine_backfill: {
        name: 'Cuisine backfill',
        description: 'Allows the automated cuisine-type backfill to run for this partner’s restaurants.',
      },
      image_backfill: {
        name: 'Image backfill',
        description: 'Allows the automated menu-image backfill to run for this partner’s restaurants.',
      },
      auto_image_generation: {
        name: 'Automatic image generation',
        description: 'Generates menu images on demand for this partner’s restaurants.',
      },
      provider_billing: {
        name: 'Partner billing by Foodamigos',
        description:
          'Includes this partner in the Foodamigos billing run. Off means the partner is invoiced outside the platform. Also turns PayPal (direct) on for this partner’s restaurants, with the service fee charged on it.',
      },
      partner_custom_dashboard_domain: {
        name: 'Own dashboard domain',
        description:
          'Lets this partner connect a domain of its own for the dashboard, so its team and restaurants sign in under the partner’s address. Switching it on or off moves every dashboard link the platform sends for this partner.',
      },
      partner_custom_shop_domain: {
        name: 'Own shop domain',
        description:
          'Lets this partner connect a domain of its own for its restaurants’ online shops, under which every restaurant gets its own address. Open it one partner at a time: it changes the address customers use.',
      },
      partner_legal_documents: {
        name: 'Partner legal documents',
        description:
          'Generates the imprint, privacy policy and terms for this partner’s restaurant shops from each restaurant’s own legal details. Off, or while a restaurant’s details are incomplete, shops show the standard platform documents.',
      },
    },
    toasts: {
      load_error: 'Failed to load the feature flags',
      error: 'Failed to change the feature flag',
      enabled: 'Feature enabled',
      disabled: 'Feature disabled',
    },
  },
};
